import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Bell, AlertTriangle, Info, AlertCircle } from "lucide-react";
import api from "../api";

// Staff alerts from notifications/notify.py (crisis alerts, reviews, supervision,
// reminders). Controlled like NotificationBell/FollowUpBell: DashboardLayout
// polls and passes the data down, since it renders the bells twice (desktop
// and mobile). Kept as its own bell so alerts are never mixed up with
// patient messages or follow-ups.
const SEVERITY = {
  urgent: { icon: AlertTriangle, cls: "text-rose-600", label: "Urgent" },
  important: { icon: AlertCircle, cls: "text-amber-600", label: "Important" },
  info: { icon: Info, cls: "text-primary-500", label: "" },
};

function ago(iso) {
  if (!iso) return "";
  const mins = Math.round((Date.now() - new Date(iso).getTime()) / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins} min ago`;
  const hrs = Math.round(mins / 60);
  if (hrs < 24) return `${hrs} h ago`;
  return new Date(iso).toLocaleDateString("en-IN", { day: "numeric", month: "short" });
}

export default function AlertsBell({ alerts = { items: [], unread_count: 0 }, onChange, iconClassName = "" }) {
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) setIsOpen(false);
    };
    document.addEventListener("click", handleClickOutside);
    return () => document.removeEventListener("click", handleClickOutside);
  }, []);

  const unread = alerts.unread_count || 0;
  const hasUrgent = alerts.items.some((n) => !n.read && n.severity === "urgent");

  const open = async (n) => {
    setIsOpen(false);
    if (!n.read) {
      try { await api.markNotificationRead(n.id); } catch { /* still navigate */ }
      if (onChange) onChange();
    }
    if (n.link) navigate(n.link);
  };

  const markAll = async () => {
    try { await api.markAllNotificationsRead(); } catch { return; }
    if (onChange) onChange();
  };

  return (
    <div className="relative" ref={containerRef}>
      <button
        type="button"
        onClick={() => setIsOpen((v) => !v)}
        className={`relative p-2 rounded-xl text-secondary-500 hover:bg-primary-50 hover:text-primary-600 transition-all ${iconClassName}`}
        title="Alerts"
        aria-label={unread ? `Alerts, ${unread} unread` : "Alerts"}
      >
        <Bell size={18} />
        {unread > 0 && (
          <span className={`absolute -top-1 -right-1 min-w-[16px] h-4 px-1 rounded-full text-white text-[10px] font-bold flex items-center justify-center leading-none ${hasUrgent ? "bg-rose-600 animate-pulse" : "bg-amber-500"}`}>
            {unread > 9 ? "9+" : unread}
          </span>
        )}
      </button>

      {isOpen && (
        <div className="absolute right-0 mt-2 w-80 bg-white rounded-xl shadow-xl ring-1 ring-black/5 overflow-hidden z-50">
          <div className="px-3 py-2 border-b border-secondary-100 flex items-center justify-between">
            <span className="text-xs font-bold text-secondary-700">Alerts</span>
            {unread > 0 && (
              <button type="button" onClick={markAll} className="text-[11px] font-semibold text-primary-600 hover:underline">Mark all as read</button>
            )}
          </div>
          <div className="max-h-96 overflow-y-auto">
            {alerts.items.length === 0 ? (
              <p className="px-3 py-4 text-xs text-secondary-400 text-center">No alerts</p>
            ) : (
              // Unread urgent alerts from the last 24 hours first, then the rest
              // newest first. (Pinning every unread urgent alert, however old,
              // buried new alerts such as "patient is waiting" under days-old ones.)
              [...alerts.items]
                .sort((a, b) => {
                  const pinned = (n) => Number(!n.read && n.severity === "urgent" && n.created_at && Date.now() - Date.parse(n.created_at) < 86400000);
                  return (pinned(b) - pinned(a)) || (Date.parse(b.created_at || 0) - Date.parse(a.created_at || 0));
                })
                .map((n) => {
                  const s = SEVERITY[n.severity] || SEVERITY.info;
                  const Icon = s.icon;
                  return (
                    <button
                      key={n.id}
                      type="button"
                      onClick={() => open(n)}
                      className={`w-full text-left px-3 py-2.5 hover:bg-primary-50 transition-colors border-b border-secondary-50 last:border-0 ${n.read ? "opacity-60" : ""}`}
                    >
                      <div className="flex items-start gap-2">
                        <Icon size={14} className={`${s.cls} mt-0.5 flex-shrink-0`} />
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center justify-between gap-2">
                            <p className="text-xs font-semibold text-secondary-800 truncate">{n.title}</p>
                            <span className="text-[10px] text-secondary-400 flex-shrink-0">{ago(n.created_at)}</span>
                          </div>
                          {n.body && <p className="text-[11px] text-secondary-500 mt-0.5 line-clamp-2">{n.body}</p>}
                          {s.label && !n.read && <span className={`text-[9px] font-bold uppercase tracking-wide ${s.cls}`}>{s.label}</span>}
                        </div>
                      </div>
                    </button>
                  );
                })
            )}
          </div>
        </div>
      )}
    </div>
  );
}
